import { appendTrackingSearch, type SearchParamsLike } from "@/core/tracking";
import {
  EMBED_PARENT_ORIGIN_PARAM,
  EMBED_PLATFORM_ID_PARAM,
  EMBED_TOKEN_PARAM,
} from "@/features/embedding/embed.utils";
import type { DisplayAgelessFlowAccessForPlatformResponse } from "@orval/model";
import { AgelessAssessmentFlowType } from "@orval/model/agelessAssessmentFlowType";

import { resolveActiveFallbackFlow } from "@/features/platform/platform.utils";

import { FLOW_SLUG_MAP, FLOW_TO_SLUG } from "./assessments.config";

export const buildAssessmentStepUrl = (
  lang: string,
  flow: AgelessAssessmentFlowType,
  subdomainSlug: string,
  step: string,
) => {
  const flowSlug = FLOW_TO_SLUG[flow];
  return `/${lang}/a/${subdomainSlug}/${flowSlug}/${step}`;
};

export const isLocaleAssessmentPath = (pathname: string, locale: string): boolean => {
  return pathname.startsWith(`/${locale}/a/`);
};

/** Resolve a URL slug to its `AgelessAssessmentFlowType`, or `undefined` if unrecognised. */
export const resolveFlowType = (slug: string): AgelessAssessmentFlowType | undefined => {
  return FLOW_SLUG_MAP[slug.toLowerCase()];
};

const PRESERVED_ASSESSMENT_PARAMS = new Set([
  "demo",
  "photo_upload",
  "at_location",
  "context",
  "locationId",
  // Embed session token. Cookie-blocking browsers (e.g. Safari builds where CHIPS
  // is disabled and the Partitioned embed cookie is dropped) prove the embed session
  // to the proxy via this query param on every full-document navigation — dropping
  // it here would downgrade the next document to `frame-ancestors 'self'` and blank
  // the customer iframe. Preserved ONLY onto assessment-path targets (see below).
  EMBED_TOKEN_PARAM,
]);

/**
 * True for canonical assessment targets (`/a/...` or `/<lang>/a/...`) — the only
 * destinations where the embed session token has a job. Escape-hatch redirects
 * (e.g. `/` when a platform lookup fails) must not carry the token onto
 * non-embed pages and into top-level history.
 */
const isAssessmentTargetPath = (path: string): boolean => /^\/(?:[^/?#]+\/)?a\//.test(path);

/**
 * Preserves tracking params AND assessment-specific feature params (e.g. `demo`)
 * through server/client redirects within the assessment flow.
 */
export const appendAssessmentSearch = (
  path: string,
  searchParams: SearchParamsLike,
  _currentPathname?: string,
): string => {
  const base = appendTrackingSearch(path, searchParams);
  const entries = Array.from(searchParams.entries());

  const extra: string[] = [];
  for (const [key, value] of entries) {
    if (key === EMBED_PLATFORM_ID_PARAM || key === EMBED_PARENT_ORIGIN_PARAM) {
      continue;
    }
    if (key === EMBED_TOKEN_PARAM && !isAssessmentTargetPath(path)) {
      continue;
    }

    if (PRESERVED_ASSESSMENT_PARAMS.has(key) && value) {
      extra.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }

  if (extra.length === 0) return base;

  const extraStr = extra.join("&");
  return base.includes("?") ? `${base}&${extraStr}` : `${base}?${extraStr}`;
};

type InactiveFlowRedirectInput = {
  readonly lang: string;
  readonly subdomainSlug: string;
  readonly platformId: string;
  readonly flowSlug: string;
  readonly flowType: AgelessAssessmentFlowType;
  readonly flowAccess: DisplayAgelessFlowAccessForPlatformResponse;
  readonly searchParams: SearchParamsLike;
};

/**
 * Where a visitor goes when the requested flow cannot start: the flow's own paused screen when the
 * subscription is paused, an active sibling flow when the practice still runs one, and the not-activated
 * screen only when nothing can be served. The search string rides along on every branch so an ad click
 * that lands on a sibling flow keeps its attribution.
 */
export async function resolveInactiveFlowRedirectUrl({
  lang,
  subdomainSlug,
  platformId,
  flowSlug,
  flowType,
  flowAccess,
  searchParams,
}: InactiveFlowRedirectInput): Promise<string> {
  const requestedFlowBase = `/${lang}/a/${subdomainSlug}/${flowSlug}`;
  if (flowAccess.isSubscriptionPaused) {
    return appendAssessmentSearch(`${requestedFlowBase}/unavailable`, searchParams);
  }

  const fallbackFlow = await resolveActiveFallbackFlow(platformId, subdomainSlug, flowType);
  if (fallbackFlow) {
    return appendAssessmentSearch(
      `/${lang}/a/${subdomainSlug}/${FLOW_TO_SLUG[fallbackFlow]}`,
      searchParams,
    );
  }

  return appendAssessmentSearch(`${requestedFlowBase}/not-activated`, searchParams);
}
